import type { Emoji } from "emojibase";

// The emoji the picker offers, from the Emojibase dataset (emojibase-data, pinned in package.json).
// The data loads only when a picker first opens, so it never adds to startup. Emoji are drawn as
// text, in the device's own emoji font.

export type EmojiEntry = {
  emoji: string;
  label: string;
  /** Words it can be found by, such as "wave" and "hello" for 👋. */
  tags: string[];
  /** The five skin tones, lightest first, where the emoji has them. */
  skins?: string[];
};

export type EmojiGroup = { key: string; label: string; icon: string; emojis: EmojiEntry[] };

/**
 * The newest emoji version offered. Newer ones show as empty boxes on many phones and computers
 * still in use, so they are left out until system fonts catch up.
 */
export const MAX_EMOJI_VERSION = 15;

/** Emojibase's groups, by number, with their names here. Group 2 is skin tone swatches, never shown. */
const GROUPS: Record<number, { key: string; label: string; icon: string }> = {
  0: { key: "smileys", label: "Smileys and emotion", icon: "😀" },
  1: { key: "people", label: "People and body", icon: "👋" },
  3: { key: "nature", label: "Animals and nature", icon: "🐻" },
  4: { key: "food", label: "Food and drink", icon: "🍔" },
  5: { key: "travel", label: "Travel and places", icon: "✈️" },
  6: { key: "activities", label: "Activities", icon: "⚽" },
  7: { key: "objects", label: "Objects", icon: "💡" },
  8: { key: "symbols", label: "Symbols", icon: "🔣" },
  9: { key: "flags", label: "Flags", icon: "🏳️" },
};

/**
 * The emoji as other apps and keyboards send it. The dataset adds a variation selector to some
 * emoji that show as emoji anyway, such as 👍. Without it, a 👍 from the picker is the same
 * reaction as a 👍 from the quick bar or another app.
 */
export function qualifiedEmoji(e: Pick<Emoji, "emoji" | "type">): string {
  const chars = [...e.emoji];
  if (e.type === 1 && chars.length === 2 && chars[1] === "\uFE0F") return chars[0] ?? e.emoji;
  return e.emoji;
}

/** Turns the dataset into the picker's groups, in Unicode's order. */
export function buildEmojiGroups(data: Emoji[]): EmojiGroup[] {
  const byGroup = new Map<number, { order: number; entry: EmojiEntry }[]>();
  for (const e of data) {
    if (e.group === undefined || !GROUPS[e.group] || e.version > MAX_EMOJI_VERSION) continue;
    const tones = (e.skins ?? []).filter((s) => typeof s.tone === "number" && s.version <= MAX_EMOJI_VERSION);
    const entry: EmojiEntry = {
      emoji: qualifiedEmoji(e),
      label: e.label,
      tags: e.tags ?? [],
      ...(tones.length === 5 ? { skins: tones.map((s) => s.emoji) } : {}),
    };
    const list = byGroup.get(e.group) ?? [];
    list.push({ order: e.order ?? 0, entry });
    byGroup.set(e.group, list);
  }
  return [...byGroup.entries()]
    .sort(([a], [b]) => a - b)
    .map(([group, list]) => ({
      ...(GROUPS[group] as { key: string; label: string; icon: string }),
      emojis: list.sort((a, b) => a.order - b.order).map((x) => x.entry),
    }));
}

let loading: Promise<EmojiGroup[]> | null = null;

/** Loads the emoji once, when a picker first needs them. A failed load is tried again next time. */
export function loadEmojiGroups(): Promise<EmojiGroup[]> {
  loading ??= import("emojibase-data/en/data.json")
    .then((m) => buildEmojiGroups(m.default as Emoji[]))
    .catch((err) => {
      loading = null;
      throw err;
    });
  return loading;
}

/** The emoji whose name or tags start with every word typed, best matches first. */
export function searchEmoji(groups: EmojiGroup[], query: string, limit = 160): EmojiEntry[] {
  const words = query.toLowerCase().trim().split(/\s+/).filter(Boolean);
  if (!words.length) return [];
  const scored: { entry: EmojiEntry; score: number; at: number }[] = [];
  let at = 0;
  for (const g of groups) {
    for (const entry of g.emojis) {
      at += 1;
      const label = entry.label.toLowerCase();
      const names = label.split(/[\s:,-]+/);
      const tags = entry.tags.map((t) => t.toLowerCase());
      const all = [...names, ...tags];
      if (!words.every((w) => all.some((n) => n.startsWith(w)) || label.includes(w))) continue;
      const first = words[0] ?? "";
      // The whole name, then a word or tag that is exactly what was typed, then a name that starts
      // with it, then anything else that matched.
      const score = label === words.join(" ") ? 0 : all.includes(first) ? 1 : label.startsWith(first) ? 2 : 3;
      scored.push({ entry, score, at });
    }
  }
  return scored
    .sort((a, b) => a.score - b.score || a.at - b.at)
    .slice(0, limit)
    .map((x) => x.entry);
}

const RECENT_KEY = "rhema.emoji.recent";
const TONE_KEY = "rhema.emoji.tone";
export const RECENT_LIMIT = 24;

/** The emoji this person picked most recently, newest first, kept on this device only. */
export function recentEmoji(): string[] {
  try {
    const list = JSON.parse(localStorage.getItem(RECENT_KEY) ?? "[]") as unknown;
    return Array.isArray(list)
      ? list.filter((e): e is string => typeof e === "string").slice(0, RECENT_LIMIT)
      : [];
  } catch {
    return [];
  }
}

export function rememberEmoji(emoji: string): string[] {
  const next = [emoji, ...recentEmoji().filter((e) => e !== emoji)].slice(0, RECENT_LIMIT);
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch {
    // Storage can be blocked. The picker still works without recent emoji.
  }
  return next;
}

/** The skin tone chosen for emoji that have them: 0 for the default yellow, 1 to 5 lightest to darkest. */
export function savedTone(): number {
  try {
    const n = Number(localStorage.getItem(TONE_KEY) ?? 0);
    return Number.isInteger(n) && n >= 0 && n <= 5 ? n : 0;
  } catch {
    return 0;
  }
}

export function saveTone(tone: number) {
  try {
    localStorage.setItem(TONE_KEY, String(tone));
  } catch {
    // Not kept, but used for this picker.
  }
}

/** The emoji in the chosen skin tone, where it has one. */
export function withTone(entry: EmojiEntry, tone: number): string {
  return (tone > 0 && entry.skins?.[tone - 1]) || entry.emoji;
}
